import React from 'react';
import Navbar from './Navbar';
import Footer from './Footer';
import { useTranslation } from '../contexts/TranslationContext';
import { ShieldCheckIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { Helmet } from 'react-helmet-async';
import { Reveal, PageHero } from './LandingShared';

/**
 * LabuanFoundation Component
 * A modern, interactive page describing the benefits of setting up a Labuan Foundation.
 */
const LabuanFoundation = () => {
  const { t } = useTranslation();
  const benefits = [
    t('labuanFoundation.benefits.item1'),
    t('labuanFoundation.benefits.item2'),
    t('labuanFoundation.benefits.item3'),
    t('labuanFoundation.benefits.item4'),
  ];

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <Helmet>
        <title>Labuan Foundation — Cogent Platform</title>
        <meta name="description" content="Learn about the benefits of setting up a Labuan Foundation through Cogent Asset Management. Asset protection, estate planning, and wealth preservation in a regulated jurisdiction." />
        <link rel="canonical" href="https://cogentplatform.com/labuan-foundation" />
      </Helmet>
      <Navbar />

      <main className="flex-grow">
        {/* Hero Banner */}
        <PageHero title={t('labuanFoundation.title')} />

        {/* Content Section */}
        <div className="bg-gradient-to-b from-white to-slate-50 py-16 sm:py-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                {/* Gradient accent line at top */}
                <div className="h-1 bg-gradient-to-r from-blue-600 to-cyan-500" />

                <div className="p-8 sm:p-12">
                  {/* Icon and Heading */}
                  <div className="text-center">
                    <Reveal delay={100}>
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center shadow-lg mx-auto">
                        <ShieldCheckIcon className="h-8 w-8 text-white" />
                      </div>
                    </Reveal>

                    <Reveal delay={200}>
                      <h2 className="mt-6 text-3xl font-bold text-gray-900">
                        {t('labuanFoundation.benefits.title')}
                      </h2>
                    </Reveal>

                    <Reveal delay={300}>
                      <div className="mt-4 w-20 h-1 bg-gradient-to-r from-blue-600 to-cyan-500 mx-auto" />
                    </Reveal>
                  </div>

                  {/* Quotes Section */}
                  <div className="mt-12 space-y-8">
                    <Reveal delay={400}>
                      <blockquote className="border-l-4 border-blue-500 bg-blue-50/50 rounded-r-lg p-6 italic text-gray-600">
                        <p>
                          {t('labuanFoundation.quote1')}
                        </p>
                      </blockquote>
                    </Reveal>

                    <Reveal delay={500}>
                      <blockquote className="border-l-4 border-blue-500 bg-blue-50/50 rounded-r-lg p-6 italic text-gray-600">
                        <p>
                          {t('labuanFoundation.quote2')}
                        </p>
                        <footer className="mt-2 text-sm text-gray-500 not-italic">
                          {t('labuanFoundation.quote2Source')}
                        </footer>
                      </blockquote>
                    </Reveal>
                  </div>

                  {/* Benefits List */}
                  <div className="mt-12 space-y-4">
                    {benefits.map((benefit, index) => (
                      <Reveal key={index} delay={600 + index * 100}>
                        <div className="flex items-start p-4 bg-gradient-to-r from-slate-50 to-blue-50/30 rounded-xl border border-gray-100">
                          <CheckCircleIcon className="flex-shrink-0 h-6 w-6 text-green-500 mr-3 mt-0.5" />
                          <span className="text-lg text-gray-700">{benefit}</span>
                        </div>
                      </Reveal>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default LabuanFoundation;
